import { baseApi } from '@ibk/shared-config';
import { VOUCHER_ONBOARDING_TYPE_WELCOME, } from '../types';
const voucherUserOnboardingTagId = (platform, type) => `voucher-user-onboarding-${platform}-${type}`;
export const benefitApi = baseApi.injectEndpoints({
    endpoints: build => ({
        getContractsByOrganization: build.query({
            query: ({ organizationId, status, sort = '-createdAt' }) => ({
                url: `/v1/voucher/contracts/organizations/${organizationId}`,
                params: {
                    status: status?.join(','),
                    sort,
                },
            }),
            transformResponse: (response) => {
                return response.result;
            },
            providesTags: ['Base'],
        }),
        getPartners: build.query({
            query: ({ benefitPartner }) => ({
                url: '/v1/voucher/partners',
                params: {
                    benefitPartner,
                },
            }),
            transformResponse: (response) => {
                return response.result.result[0];
            },
            providesTags: ['Base'],
        }),
        createContract: build.mutation({
            query: ({ organizationId, voucherPartnerId, locationId, productFamily }) => ({
                url: '/v1/voucher/contracts',
                method: 'POST',
                body: {
                    organizationId,
                    voucherPartnerId,
                    locationId,
                    ...(productFamily && { productFamily }),
                },
            }),
            invalidatesTags: ['Base'],
        }),
        addProductFamiliesToContract: build.mutation({
            query: ({ voucherContractId, productFamily }) => ({
                url: `/v1/voucher/contracts/${voucherContractId}/vouchers/families`,
                method: 'POST',
                body: {
                    productFamily,
                },
            }),
            invalidatesTags: ['Base'],
        }),
        getVouchersByContract: build.query({
            query: ({ voucherContractId }) => ({
                url: `/v1/voucher/contracts/${voucherContractId}/vouchers`,
            }),
            transformResponse: (response) => {
                return response.result;
            },
            providesTags: (result, error, { voucherContractId }) => [
                { type: 'Base', id: `voucher-contract-details-${voucherContractId}` },
                { type: 'Base', id: 'voucher-contract-details' },
            ],
        }),
        getVoucherContractSummary: build.query({
            query: ({ voucherContractId }) => ({
                url: `/v1/voucher/contracts/${voucherContractId}/summary`,
            }),
            transformResponse: (response) => response.result,
            providesTags: (result, error, { voucherContractId }) => [
                { type: 'Base', id: `voucher-contract-summary-${voucherContractId}` },
                { type: 'Base', id: 'voucher-contract-summary' },
            ],
        }),
        getVoucherContractConfigs: build.query({
            query: ({ voucherContractId }) => ({
                url: `/v1/voucher/contracts/${voucherContractId}/configs`,
            }),
            transformResponse: (response) => response.result,
            providesTags: (result, error, { voucherContractId }) => [
                { type: 'Base', id: `voucher-contract-configs-${voucherContractId}` },
            ],
        }),
        updateVoucherContractConfig: build.mutation({
            query: ({ voucherContractId, configId, isEnable }) => ({
                url: `/v1/voucher/contracts/${voucherContractId}/configs/${configId}`,
                method: 'PUT',
                body: { isEnable },
            }),
            invalidatesTags: (result, error, { voucherContractId }) => [
                error ? undefined : { type: 'Base', id: `voucher-contract-configs-${voucherContractId}` },
            ],
        }),
        getRechargesByContract: build.query({
            query: ({ contractId, limit = 10, offset = 0, status, sort = '-createdAt' }) => ({
                url: `/v1/voucher/contracts/${contractId}/recharges`,
                params: {
                    limit,
                    offset,
                    status: status?.join(','),
                    sort,
                },
            }),
            transformResponse: (response) => {
                return response.result;
            },
            providesTags: (result, error, { contractId }) => [
                { type: 'VoucherRecharge', id: contractId },
                { type: 'VoucherRecharge', id: 'LIST' },
            ],
        }),
        getVoucherBeneficiaries: build.query({
            query: ({ voucherContractId, voucherTypeId }) => ({
                url: `/v1/voucher/contracts/${voucherContractId}/vouchers/${voucherTypeId}/beneficiaries`,
            }),
            transformResponse: (response) => {
                return response.result;
            },
            providesTags: (result, error, { voucherContractId, voucherTypeId }) => [
                { type: 'Base', id: `voucher-beneficiaries-${voucherContractId}-${voucherTypeId}` },
                { type: 'Base', id: 'voucher-beneficiaries' },
            ],
        }),
        getContractBeneficiaries: build.query({
            query: ({ voucherContractId, limit, offset, status, sort }) => ({
                url: `/v1/voucher/contracts/${voucherContractId}/beneficiaries`,
                params: {
                    limit,
                    offset,
                    status,
                    sort,
                },
            }),
            transformResponse: (response) => {
                return response.result;
            },
            keepUnusedDataFor: 120,
            providesTags: (result, error, { voucherContractId }) => [
                { type: 'Base', id: `contract-beneficiaries-${voucherContractId}` },
                { type: 'Base', id: 'contract-beneficiaries' },
            ],
        }),
        simulateVoucherContractRecharge: build.mutation({
            query: ({ voucherContractId, body }) => ({
                url: `/v3/voucher/contracts/${voucherContractId}/recharges/simulate`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error, { voucherContractId }) => [
                { type: 'VoucherRecharge', id: voucherContractId },
            ],
        }),
        confirmVoucherContractRecharge: build.mutation({
            query: ({ voucherContractId, body }) => ({
                url: `/v3/voucher/contracts/${voucherContractId}/recharges/confirm`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error, { voucherContractId }) => [
                { type: 'VoucherRecharge', id: voucherContractId },
                { type: 'VoucherRecharge', id: 'LIST' },
                'Base',
            ],
        }),
        updateVoucherContractDetail: build.mutation({
            query: ({ voucherContractId, voucherType, body }) => ({
                url: `/v2/voucher/contracts/${voucherContractId}/vouchers/${voucherType}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (result, error, { voucherContractId }) => [
                { type: 'Base', id: `voucher-contract-details-${voucherContractId}` },
                { type: 'Base', id: 'voucher-contract-details' },
            ],
        }),
        updateVoucherBeneficiaries: build.mutation({
            query: ({ voucherContractId, voucherTypeId, body }) => ({
                url: `/v1/voucher/contracts/${voucherContractId}/vouchers/${voucherTypeId}/beneficiaries`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (result, error, { voucherContractId, voucherTypeId }) => [
                { type: 'Base', id: `voucher-beneficiaries-${voucherContractId}-${voucherTypeId}` },
            ],
        }),
        getRechargeById: build.query({
            query: ({ voucherContractId, rechargeId }) => ({
                url: `/v2/voucher/contracts/${voucherContractId}/recharges/${rechargeId}`,
            }),
            transformResponse: (response) => {
                return response.result;
            },
            providesTags: (result, error, { voucherContractId, rechargeId }) => [
                { type: 'VoucherRecharge', id: `${voucherContractId}-${rechargeId}` },
                { type: 'VoucherRecharge', id: 'DETAIL' },
            ],
        }),
        getRechargePaymentCode: build.query({
            query: ({ voucherContractId, voucherContractRechargeId }) => ({
                url: `/v2/voucher/contracts/${voucherContractId}/recharges/${voucherContractRechargeId}/payment-code`,
            }),
            transformResponse: (response) => {
                return response.result;
            },
            providesTags: (result, error, { voucherContractId, voucherContractRechargeId }) => [
                { type: 'VoucherRecharge', id: `payment-${voucherContractId}-${voucherContractRechargeId}` },
                { type: 'VoucherRecharge', id: 'PAYMENT' },
            ],
        }),
        getRechargeBankSlip: build.query({
            query: ({ voucherContractId, voucherContractRechargeId }) => ({
                url: `/v1/voucher/contracts/${voucherContractId}/recharges/${voucherContractRechargeId}/bank-slip`,
            }),
            providesTags: (result, error, { voucherContractId, voucherContractRechargeId }) => [
                { type: 'VoucherRecharge', id: `bankslip-${voucherContractId}-${voucherContractRechargeId}` },
                { type: 'VoucherRecharge', id: 'BANKSLIP' },
            ],
        }),
        getRechargeReport: build.query({
            query: ({ voucherContractId, voucherContractRechargeId }) => ({
                url: `/v1/voucher/contracts/${voucherContractId}/recharges/${voucherContractRechargeId}/report`,
                method: 'POST',
            }),
            providesTags: (result, error, { voucherContractId, voucherContractRechargeId }) => [
                { type: 'VoucherRecharge', id: `report-${voucherContractId}-${voucherContractRechargeId}` },
                { type: 'VoucherRecharge', id: 'REPORT' },
            ],
        }),
        getBusinessDays: build.query({
            query: ({ competence }) => ({
                url: '/v1/voucher/contracts/business-days',
                params: {
                    competence,
                },
            }),
            transformResponse: (response) => {
                return response.result;
            },
            providesTags: ['Base'],
        }),
        getRechargeHistory: build.query({
            query: ({ contractId, limit = 100, orderId, search, sort = '-createdAt' }) => ({
                url: `/v1/voucher/contracts/${contractId}/recharges`,
                params: {
                    limit: Math.min(limit, 100),
                    sort,
                    ...(orderId && { orderId: orderId.replace(/\D/g, '') }),
                    ...(search && { search }),
                },
            }),
            transformResponse: (response) => {
                const apiData = response.result;
                return {
                    total: apiData.total,
                    result: apiData.result,
                };
            },
            providesTags: (result, error, { contractId }) => [
                { type: 'VoucherRecharge', id: `history-${contractId}` },
                { type: 'VoucherRecharge', id: 'HISTORY' },
            ],
        }),
        updateRechargeData: build.mutation({
            query: ({ voucherContractId, rechargeId, body }) => ({
                url: `/v1/voucher/contracts/${voucherContractId}/recharges/${rechargeId}`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (result, error, { voucherContractId, rechargeId }) => [
                { type: 'VoucherRecharge', id: `${voucherContractId}-${rechargeId}` },
                { type: 'VoucherRecharge', id: 'DETAIL' },
                { type: 'VoucherRecharge', id: voucherContractId },
                { type: 'VoucherRecharge', id: 'LIST' },
            ],
        }),
        cancelRecharge: build.mutation({
            query: ({ voucherContractId, rechargeId }) => ({
                url: `/v1/voucher/contracts/${voucherContractId}/recharges/${rechargeId}/cancel`,
                method: 'POST',
            }),
            invalidatesTags: (result, error, { voucherContractId, rechargeId }) => [
                { type: 'VoucherRecharge', id: `${voucherContractId}-${rechargeId}` },
                { type: 'VoucherRecharge', id: 'DETAIL' },
                { type: 'VoucherRecharge', id: voucherContractId },
                { type: 'VoucherRecharge', id: 'LIST' },
                { type: 'VoucherRecharge', id: `history-${voucherContractId}` },
                { type: 'VoucherRecharge', id: 'HISTORY' },
            ],
        }),
        getTrackingCards: build.query({
            query: ({ voucherContractId }) => ({
                url: `/v1/voucher/contracts/${voucherContractId}/cards/tracking`,
            }),
            transformResponse: (response) => {
                return response.result;
            },
            providesTags: (result, error, { voucherContractId }) => [
                { type: 'Base', id: `tracking-cards-${voucherContractId}` },
                { type: 'Base', id: 'tracking-cards' },
            ],
        }),
        getCardsTracking: build.query({
            query: ({ voucherContractId, trackingCode }) => ({
                url: `/v1/voucher/contracts/${voucherContractId}/cards/tracking/${trackingCode}`,
            }),
            transformResponse: (response) => response.result,
            providesTags: (result, error, { voucherContractId, trackingCode }) => [
                { type: 'Base', id: `cards-tracking-${voucherContractId}-${trackingCode}` },
                { type: 'Base', id: 'cards-tracking' },
            ],
        }),
        getTrackingBeneficiaries: build.query({
            query: ({ voucherContractId, unlockCode }) => {
                const queryString = unlockCode.map(code => `unlockCode=${encodeURIComponent(code)}`).join('&');
                return {
                    url: `/v1/voucher/contracts/${voucherContractId}/cards/tracking/beneficiaries?${queryString}`,
                };
            },
            transformResponse: (response) => response.result,
            providesTags: (result, error, { voucherContractId, unlockCode }) => [
                { type: 'Base', id: `tracking-beneficiaries-${voucherContractId}-${unlockCode.join(',')}` },
                { type: 'Base', id: 'tracking-beneficiaries' },
            ],
        }),
        getVoucherContractPdf: build.mutation({
            query: ({ voucherContractId, productFamily }) => ({
                url: `/v1/voucher/contracts/${voucherContractId}/contract`,
                method: 'POST',
                body: { productFamily },
            }),
            transformResponse: (response) => response.result,
        }),
        getDeliveryLocationsByContract: build.query({
            query: ({ voucherContractId }) => ({
                url: `/v1/voucher/contracts/${voucherContractId}/delivery-locations`,
            }),
            transformResponse: (response) => {
                return response.result;
            },
            providesTags: (result, error, { voucherContractId }) => [
                { type: 'Base', id: `delivery-locations-${voucherContractId}` },
                { type: 'Base', id: 'delivery-locations' },
            ],
        }),
        createDeliveryLocation: build.mutation({
            query: ({ voucherContractId, body }) => ({
                url: `/v1/voucher/contracts/${voucherContractId}/delivery-locations`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error, { voucherContractId }) => [
                error ? undefined : { type: 'Base', id: `delivery-locations-${voucherContractId}` },
                error ? undefined : { type: 'Base', id: 'delivery-locations' },
            ],
        }),
        updateDeliveryLocation: build.mutation({
            query: ({ voucherContractId, deliveryLocationId, body }) => ({
                url: `/v1/voucher/contracts/${voucherContractId}/delivery-locations/${deliveryLocationId}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (result, error, { voucherContractId }) => [
                error ? undefined : { type: 'Base', id: `delivery-locations-${voucherContractId}` },
                error ? undefined : { type: 'Base', id: 'delivery-locations' },
            ],
        }),
        deleteDeliveryLocation: build.mutation({
            query: ({ voucherContractId, deliveryLocationId }) => ({
                url: `/v1/voucher/contracts/${voucherContractId}/delivery-locations/${deliveryLocationId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, { voucherContractId }) => [
                error ? undefined : { type: 'Base', id: `delivery-locations-${voucherContractId}` },
                error ? undefined : { type: 'Base', id: 'delivery-locations' },
            ],
        }),
        activateDeliveryLocation: build.mutation({
            query: ({ voucherContractId, deliveryLocationId }) => ({
                url: `/v1/voucher/contracts/${voucherContractId}/delivery-locations/${deliveryLocationId}/activate`,
                method: 'POST',
            }),
            invalidatesTags: (result, error, { voucherContractId }) => [
                error ? undefined : { type: 'Base', id: `delivery-locations-${voucherContractId}` },
                error ? undefined : { type: 'Base', id: 'delivery-locations' },
            ],
        }),
        getBeneficiaryFilters: build.query({
            query: ({ voucherContractId }) => ({
                url: `/v1/voucher/contracts/${voucherContractId}/beneficiaries/filters`,
            }),
            transformResponse: (response) => {
                return response.result;
            },
            providesTags: (result, error, { voucherContractId }) => [
                error ? undefined : { type: 'Base', id: `beneficiary-filters-${voucherContractId}` },
                error ? undefined : { type: 'Base', id: 'beneficiary-filters' },
            ],
        }),
        searchBeneficiaries: build.query({
            query: ({ voucherContractId, voucherTypeId, body }) => ({
                url: `/v2/voucher/contracts/${voucherContractId}/vouchers/${voucherTypeId}/beneficiaries`,
                method: 'POST',
                body,
            }),
            transformResponse: (response) => {
                return response.result;
            },
            providesTags: (result, error, { voucherContractId, voucherTypeId }) => [
                { type: 'Base', id: `voucher-beneficiaries-${voucherContractId}-${voucherTypeId}` },
                { type: 'Base', id: 'voucher-beneficiaries' },
            ],
        }),
        validateRechargeSpreadsheet: build.mutation({
            query: ({ voucherContractId, voucherTypeId, releaseType, discountType, file }) => {
                const body = new FormData();
                body.append('voucherTypeId', voucherTypeId);
                body.append('releaseType', releaseType);
                body.append('discountType', discountType);
                body.append('file', file);
                return {
                    url: `/v1/voucher/contracts/${voucherContractId}/recharges/spreadsheet/validate`,
                    method: 'POST',
                    body,
                };
            },
            transformResponse: (response) => response.result,
        }),
        getUserVoucherOnboarding: build.query({
            query: ({ platform, type = VOUCHER_ONBOARDING_TYPE_WELCOME }) => ({
                url: '/v1/voucher/user-onboarding',
                params: { platform, type },
            }),
            transformResponse: (response) => response.result ?? [],
            providesTags: (_result, _error, { platform, type = VOUCHER_ONBOARDING_TYPE_WELCOME }) => [
                { type: 'Base', id: voucherUserOnboardingTagId(platform, type) },
            ],
        }),
        completeUserVoucherOnboarding: build.mutation({
            query: ({ platform, type }) => ({
                url: '/v1/voucher/user-onboarding',
                method: 'POST',
                body: { platform, type },
            }),
            invalidatesTags: (_result, _error, { platform, type }) => [
                { type: 'Base', id: voucherUserOnboardingTagId(platform, type) },
            ],
        }),
        createContactRequest: build.mutation({
            query: body => ({
                url: '/v1/voucher/contact-requests',
                method: 'POST',
                body,
            }),
        }),
    }),
    overrideExisting: false,
});
export const { useGetContractsByOrganizationQuery, useGetVouchersByContractQuery, useGetVoucherContractSummaryQuery, useGetVoucherContractConfigsQuery, useUpdateVoucherContractConfigMutation, useGetRechargesByContractQuery, useGetRechargeByIdQuery, useLazyGetRechargeByIdQuery, useGetRechargePaymentCodeQuery, useLazyGetRechargePaymentCodeQuery, useGetRechargeBankSlipQuery, useLazyGetRechargeBankSlipQuery, useGetRechargeReportQuery, useLazyGetRechargeReportQuery, useGetVoucherBeneficiariesQuery, useGetContractBeneficiariesQuery, useSimulateVoucherContractRechargeMutation, useConfirmVoucherContractRechargeMutation, useUpdateVoucherContractDetailMutation, useUpdateVoucherBeneficiariesMutation, useUpdateRechargeDataMutation, useCancelRechargeMutation, useGetBusinessDaysQuery, useGetRechargeHistoryQuery, useLazyGetRechargeHistoryQuery, useGetTrackingCardsQuery, useGetCardsTrackingQuery, useGetTrackingBeneficiariesQuery, useGetVoucherContractPdfMutation, useGetDeliveryLocationsByContractQuery, useCreateDeliveryLocationMutation, useUpdateDeliveryLocationMutation, useDeleteDeliveryLocationMutation, useActivateDeliveryLocationMutation, useGetBeneficiaryFiltersQuery, useSearchBeneficiariesQuery, useLazySearchBeneficiariesQuery, useAddProductFamiliesToContractMutation, useValidateRechargeSpreadsheetMutation, useGetUserVoucherOnboardingQuery, useCompleteUserVoucherOnboardingMutation, useCreateContactRequestMutation, } = benefitApi;
